import axios from 'axios';

// Ensure base URL ends with /api so paths like /member-portal/payment-categories resolve correctly
// In production (same-origin): use '' so API calls go to /api on the same domain
const rawBase = process.env.REACT_APP_API_BASE_URL || process.env.VITE_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
const API_BASE_URL = rawBase ? (rawBase.replace(/\/api\/?$/, '') + '/api') : '/api';

export { API_BASE_URL };

/** Base URL for uploads. When using protected uploads, use API path. */
export const UPLOADS_BASE = rawBase ? rawBase.replace(/\/api\/?$/, '') : (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5001');

/** Public URL for verified member photo (QR verify page; no auth). Only works when card is approved. */
export function memberVerifyPhotoUrl(idParam) {
  if (idParam == null || idParam === '') return null;
  return `/api/member-portal/verify/${encodeURIComponent(String(idParam))}/photo`;
}

/** Path for protected uploads (requires auth). Use with api.get() or AuthImage. */
export function uploadsApiPath(filename) {
  if (!filename) return null;
  return `/uploads/${filename.replace(/^\/+/, '')}`;
}

/**
 * Full URL for an uploaded file. For public static: /uploads/xxx.
 * For protected mode (PROTECT_UPLOADS_ONLY): use AuthImage with uploadsApiPath() instead.
 */
export function uploadsUrl(filename) {
  if (!filename) return null;
  const base = UPLOADS_BASE.replace(/\/$/, '');
  const clean = filename.replace(/^\/+/, '');
  return filename.startsWith('http') ? filename : `${base}/uploads/${clean}`;
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and fix URL (axios treats /path as absolute, losing baseURL's /api)
api.interceptors.request.use(
  (config) => {
    // Don't set Content-Type for FormData - let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    // Fix: paths starting with / cause axios to drop baseURL path; use relative path
    if (config.url?.startsWith('/') && config.baseURL?.includes('/api')) {
      config.url = config.url.replace(/^\//, '');
    }
    // Use appropriate token: member portal, protected uploads, or admin
    const urlPath = config.url || '';
    const isMemberPortal = urlPath.includes('member-portal');
    // Only the public station API (/api/kiosk/...), not HR paths like .../kiosk-passcode
    const isKioskApi =
      urlPath === 'kiosk' ||
      urlPath.startsWith('kiosk/') ||
      urlPath.includes('/kiosk/');
    // After leading / is stripped, paths are like "uploads/profile-xxx.jpg" (no "/uploads" substring)
    const isProtectedUpload =
      urlPath.startsWith('uploads/') || urlPath.includes('/uploads/');
    const memberToken = localStorage.getItem('memberToken');
    const token = localStorage.getItem('token');
    const kioskToken =
      typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('kioskStationToken') : null;

    if (isProtectedUpload) {
      // Prefer staff token so admin pages (AuthImage) are not overridden by a stale member-portal session
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else if (memberToken) {
        config.headers.Authorization = `Bearer ${memberToken}`;
        config.headers['x-auth-token'] = memberToken;
      }
    } else if (
      isKioskApi &&
      kioskToken &&
      !urlPath.includes('kiosk/arm') &&
      !(urlPath === 'kiosk/elections' || urlPath.endsWith('/kiosk/elections'))
    ) {
      config.headers.Authorization = `Bearer ${kioskToken}`;
      config.headers['X-Kiosk-Token'] = kioskToken;
    } else if (isMemberPortal && memberToken) {
      config.headers.Authorization = `Bearer ${memberToken}`;
      config.headers['x-auth-token'] = memberToken;
    }
    if (!config.headers.Authorization && token && !isKioskApi) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Ensure response has proper structure
    if (response && response.data) {
      return response;
    }
    return response;
  },
  async (error) => {
    // Ensure error has proper structure
    const errorResponse = {
      ...error,
      response: error.response || {
        status: error.status || 500,
        data: {
          error: true,
          message: error.message || 'An error occurred',
          ...(error.response?.data || {})
        }
      },
      message: error.message || 'Request failed'
    };

    if (errorResponse.response?.status === 401) {
      const url = error.config?.url || '';
      const isMemberPortal = url.includes('member-portal');
      const isKioskApi =
        url === 'kiosk' || url.startsWith('kiosk/') || url.includes('/kiosk/');
      if (isKioskApi) {
        // Stay on /kiosk — UI clears station session and shows arm screen
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem('kioskStationToken');
          sessionStorage.removeItem('kioskStationMeta');
        }
      } else if (isMemberPortal) {
        localStorage.removeItem('memberToken');
        window.location.href = '/member-portal/login';
      } else {
        localStorage.removeItem('token');
        window.location.href = '/';
      }
    }
    if (
      errorResponse.response?.status === 403 &&
      !error.config?.url?.includes('member-portal') &&
      !(
        (error.config?.url || '').startsWith('kiosk/') ||
        (error.config?.url || '').includes('/kiosk/')
      )
    ) {
      const requiredPermission = errorResponse.response?.data?.requiredPermission;
      const msg = errorResponse.response?.data?.message || 'You do not have permission to access this area';

      // Retry once after refreshing permissions (handles stale session after admin grants access)
      if (requiredPermission && !error.config?.__403Retried) {
        try {
          const meRes = await api.get('/auth/me');
          const perms = meRes.data?.staff?.permissions || [];
          if (perms.includes('all') || perms.includes(requiredPermission)) {
            error.config.__403Retried = true;
            // Notify AuthContext to refresh staff so sidebar/menu updates
            if (typeof window !== 'undefined' && window.dispatchEvent) {
              window.dispatchEvent(new CustomEvent('auth:permissions-refreshed'));
            }
            return api.request(error.config);
          }
        } catch {
          // Refresh failed, fall through to redirect
        }
      }

      if (typeof window !== 'undefined' && !window.__403Handled) {
        window.__403Handled = true;
        setTimeout(() => { window.__403Handled = false; }, 1000);
        window.location.href = '/dashboard?error=' + encodeURIComponent(msg);
      }
    }
    return Promise.reject(errorResponse);
  }
);

export default api;
